import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment, { Moment } from 'moment';
import {
  Typography,
  Button,
  Input,
  InputNumber,
  InputPrice,
  BorderLine,
  DatePicker,
  enqueueSnackbar,
  Select,
} from '../../design-system';
import { DashboardHeader, DashboardForm, DashboardFormCustomColumn } from '../dashboard';
import {
  useAddPricingMutation,
  useConvertCurrencyFcQuery,
  useStoragePricingForm,
} from '../../hooks';
import { formatContainerType, getNormalFormProps } from '../../utils';
import { getCache } from '../../services';
import {
  MESSAGES,
  INPUT_ERRORS,
  pricingDetails,
  pricingFormLabels,
  containerTypes,
  Pricing,
  currencyOptions,
  CURRENCIES,
} from '../../const';
import { StoragePricingsAddCache, StoragePricingAddFormProps, ContainerType } from '../../types';
import { i18n } from '../../i18n';

let formProps = {} as StoragePricingAddFormProps;

const getSubmitProps = () => {
  const validate = {
    freeDays: String(formProps.freeDays).length,
    ...['loaded', 'empty', 'loadedCraneIn', 'loadedCraneOut', 'emptyCraneIn', 'emptyCraneOut'].map(
      (key) => ({
        [key]: containerTypes
          .map((containerType) => formProps[containerType.value][key as keyof Pricing])
          .every((val: number) => String(val).length !== 0),
      }),
    ),
  };

  if ([...Object.values(validate)].some((val) => !val)) {
    return;
  }

  return getNormalFormProps(formProps);
};

export const StoragePricingsAddHeader = ({ showOnlySubmit }: any) => {
  const params = useParams();
  const navigate = useNavigate();
  const { mutate } = useAddPricingMutation();

  const handleSubmitClick = () => {
    formProps.onSubmitClick();
    const submitProps = getSubmitProps();
    if (!submitProps) return;

    mutate(
      { ...submitProps, id: params.id },
      {
        onSuccess: () => {
          enqueueSnackbar(MESSAGES.STORAGE_PRICING.SUCCESS.ADDED, 'success');
          navigate('/dashboard/storage-pricing');
        },
      },
    );
  };

  return (
    <DashboardHeader>
      {!showOnlySubmit && (
        <>
          <Typography variant={'textXL'} weight={'bold'}>
            {i18n.t('pages.storagePricingAdd.title')}
          </Typography>
          <Button size={'medium'} data-color={'white'} to={-1}>
            {i18n.t('button.goBack')}
          </Button>
        </>
      )}
      {showOnlySubmit && <span />}
      <Button size={'medium'} onClick={handleSubmitClick}>
        {i18n.t('button.submit')}
      </Button>
    </DashboardHeader>
  );
};

export const StoragePricingsAddForm = () => {
  const convertCurrency = useConvertCurrencyFcQuery();
  const [yardName] = useState(getCache<StoragePricingsAddCache>('storage-pricing-add')?.yardName);
  const [clientName] = useState(
    getCache<StoragePricingsAddCache>('storage-pricing-add')?.clientName,
  );
  const [{ effectiveDate, freeDays, currencyCode, ...containers }, setPricingState] =
    useStoragePricingForm({
      effectiveDate: moment(),
      currencyCode: 'USD',
    });
  const [updateKey, setUpdateKey] = useState(Math.random());

  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  useEffect(() => {
    formProps = {
      ...formProps,
      yardName,
      clientName,
      effectiveDate,
      freeDays,
      currencyCode,
      ...containers,
      onSubmitClick: () => {
        setIsSubmitClicked(true);
      },
    };
  }, [
    yardName,
    clientName,
    effectiveDate,
    freeDays,
    currencyCode,
    ...pricingDetails.map((detail) =>
      Object.keys(containers)
        .map((containerType) => containers[containerType as ContainerType][detail])
        .join(''),
    ),
  ]);

  const handleEffectiveDateClick = (pricingDate: Moment) => {
    setPricingState((props) => {
      props.effectiveDate = pricingDate;
    });
  };

  const handleCurrencyChange = (e: any) => {
    const oldCurrencyCode = currencyCode;
    const newCurrencyCode = e.target.value;

    setPricingState((props) => {
      props.currencyCode = e.target.value;

      (Object.keys(containers) as Array<keyof typeof containers>).forEach((containerType) => {
        pricingDetails.forEach((detail) => {
          const value = props[containerType][detail];
          const convertedValue = convertCurrency(oldCurrencyCode, newCurrencyCode, value);
          props[containerType][detail] = Number(convertedValue.toFixed(2));
        });
      });

      setUpdateKey(Math.random());
    });
  };

  return (
    <DashboardForm paperSize={'full-size'}>
      <DashboardFormCustomColumn columns={'1fr 1fr 1fr 1fr'}>
        <Input
          label={i18n.t('common.client')}
          data-size={'medium'}
          defaultValue={clientName}
          readOnly
        />
        <Input
          label={i18n.t('common.yard')}
          data-size={'medium'}
          defaultValue={yardName}
          readOnly
        />
        <DatePicker
          label={i18n.t('label.effectiveDate')}
          data-size={'medium'}
          defaultValue={effectiveDate}
          onChange={handleEffectiveDateClick}
          withoutClearIcon
        />
        <InputNumber
          label={i18n.t('label.freeDays')}
          data-size={'medium'}
          defaultValue={freeDays}
          onChange={(e) =>
            setPricingState((props) => {
              props.freeDays = Number(e.target.value);
            })
          }
          error={isSubmitClicked ? !String(freeDays).length : false}
          helperText={
            isSubmitClicked ? (!String(freeDays).length ? INPUT_ERRORS.REQUIRED.FREE_DAYS : '') : ''
          }
        />
        <Select
          label={i18n.t('label.currencyCode')}
          options={currencyOptions}
          value={currencyCode}
          onChange={handleCurrencyChange}
        />
      </DashboardFormCustomColumn>
      <BorderLine space={'dashboard-form'} />
      {Object.keys(containers).map((containerType, i) => (
        <DashboardFormCustomColumn
          key={`container-${i}-${updateKey}`}
          columns={'1fr 1fr 1fr 1fr 1fr 1fr'}
          align={'start'}
        >
          {pricingDetails.map((detail, n) => (
            <InputPrice
              key={`detail-${n}`}
              label={pricingFormLabels[detail](formatContainerType(containerType as ContainerType))}
              data-size={'medium'}
              defaultValue={containers[containerType as ContainerType][detail]}
              onChange={(e) => {
                setPricingState((props) => {
                  props[containerType as ContainerType][detail] = Number(e.target.value);
                });
              }}
              endIcon={CURRENCIES[currencyCode].symbol}
              error={
                isSubmitClicked
                  ? !String(containers[containerType as ContainerType][detail]).length
                  : false
              }
              helperText={
                isSubmitClicked
                  ? !String(containers[containerType as ContainerType][detail]).length
                    ? INPUT_ERRORS.REQUIRED.PRICE
                    : ''
                  : ''
              }
            />
          ))}
        </DashboardFormCustomColumn>
      ))}
    </DashboardForm>
  );
};
